import { ReactNode } from 'react';
import { Box, Typography, Button, alpha, useTheme } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { TYPE } from '@/theme/typography';

interface EmptyStateProps {
  /** Icon component (e.g. from @mui/icons-material) — falls back to InboxOutlined */
  icon?: ReactNode;
  /** One-line title, e.g. "No employees yet" */
  title: string;
  /** Longer description, 1-2 sentences */
  description?: string;
  /** Primary action button label */
  actionLabel?: string;
  /** Action click handler */
  onAction?: () => void;
  /** Secondary text under action (e.g. help link) */
  secondary?: ReactNode;
  /** Use a compact layout — no outer card/padding, for embedding inside small containers */
  dense?: boolean;
}

export default function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondary,
  dense = false,
}: EmptyStateProps) {
  const theme = useTheme();
  const resolvedIcon = icon ?? <InboxOutlinedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: dense ? 4 : 8,
        px: 3,
        animation: 'fadeInUp 0.5s ease-out',
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: dense ? 72 : 96,
          height: dense ? 72 : 96,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.12),
          mb: 2,
        }}
      >
        {resolvedIcon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: TYPE.semibold, color: 'text.primary', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', maxWidth: 360, mb: actionLabel || secondary ? 2.5 : 0 }}
        >
          {description}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction} sx={{ textTransform: 'none' }}>
          {actionLabel}
        </Button>
      )}
      {secondary && (
        <Box sx={{ mt: actionLabel ? 1.5 : 2 }}>{secondary}</Box>
      )}
    </Box>
  );
}
